import React from "react";
//#region types
import { EditableFormInnerWrapperProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import useFormContext from "./context/useFormContext";
import GenericFormInner from "./genericFormInner";
import { useFormikContext } from "formik";
import { FieldConstants } from "utils/constants";
import useCheckVisibility from "./hooks/useCheckVisibility";
//#endregion
import { FormHelper } from "utils/helpers";
import { useDebouncedCallback } from "use-debounce";

function GenericFormInnerContainer<T extends Record<string, any>>(props: EditableFormInnerWrapperProps<T>) {
  const { values, isValid, touched } = useFormikContext<any>();
  const { autogeneratedValues, initialValues } = props;
  const { getFieldHidden } = useCheckVisibility<T>();
  const previousSectionsRef = React.useRef<string>();

  const formContext = useFormContext();

  React.useEffect(() => {
    formContext?.setAutogeneratedValues(autogeneratedValues);
  }, [autogeneratedValues]);

  const debouncedonFormUpdate = useDebouncedCallback((values: any, isValid: boolean) => {
    props.onFormUpdate && props.onFormUpdate(values, isValid);
  }, FieldConstants.DebounceTime);

  React.useEffect(() => {
    debouncedonFormUpdate(values, isValid);

    if (props.disableSubmitOn && props.onFormValidationChange) {
      const isInvalid = props.disableSubmitOn(values);
      props.onFormValidationChange(!isInvalid);
    }
  }, [values]);

  React.useEffect(() => {
    const isFormDirty =
      initialValues && values
        ? FormHelper.isFormChanged(initialValues, values, [...Object.keys(initialValues), ...Object.keys(values)])
        : false;
    props.onFormDirtyChange && props.onFormDirtyChange(isFormDirty);
  }, [touched, values]);

  // Update navigation sections dynamically based on current form values
  // Use ref comparison to prevent infinite loops - only update if sections actually changed
  React.useEffect(() => {
    if (props.setNavLayoutSections && Array.isArray(props.fields)) {
      const newSections = props.fields
        .filter((x) => !getFieldHidden(x.hidden))
        .map((x) => ({
          key: FormHelper.getSectionNavKey(x),
          label: x.label,
          icon: x.icon,
        }));
      
      // Stringify for comparison to avoid object reference issues
      const newSectionsString = JSON.stringify(newSections);
      
      // Only update if sections actually changed
      if (previousSectionsRef.current !== newSectionsString) {
        previousSectionsRef.current = newSectionsString;
        props.setNavLayoutSections(newSections);
      }
    }
  }, [values, props.fields, props.setNavLayoutSections, getFieldHidden]);

  return <GenericFormInner<T> {...props} />;
}

export default GenericFormInnerContainer;
